import {
  ActionError,
  AttentionBlock,
  Button,
  EmptyState,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  ReorderControls,
  SavedIndicator,
  Skeleton,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import {
  usePublishClinicQuestions,
  useQuestionnaire,
  type Question,
  type Questionnaire,
} from './api'
import { PrintBlankFormButton } from './print-blank-form'
import { QuestionForm } from './question-form'
import { ConfirmationReadBlock, QuestionRow, SectionReadBlock } from './question-summary'
import {
  draftAsQuestion,
  emptyQuestion,
  fromQuestion,
  moveItem,
  serverErrorsAt,
  toClinicQuestionsInput,
  validateQuestion,
  type QuestionDraft,
} from './questionnaire-form'
import { questionCountLine } from './questionnaire-labels'

/**
 * Questionário da anamnese, para a gestora (docs/areas/anamnese.md, "Telas · Questionário",
 * P-ANA-63; RN-ANA-19). Arquétipo: cadastro. A decisão: "o que a minha paciente responde além do
 * padrão?". O padrão da Westhetics aparece inteiro, em leitura, e só as perguntas da clínica, no fim
 * de "Sua saúde", se editam. Uma ação primária: "Publicar perguntas"; cada publicação é uma versão
 * nova, e as anamneses já enviadas não mudam (RN-ANA-17). No cabeçalho, "Imprimir ficha em branco"
 * baixa o questionário vigente para a paciente preencher à mão (RN-ANA-24).
 */

const CLINIC_LIMIT = 10

export function QuestionnairePage() {
  const query = useQuestionnaire()
  const isDesk = useBreakpoint('desk')
  // A data da última publicação só é conhecida depois de publicar aqui: o GET traz só a versão.
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  if (query.isError || query.isPending) {
    return (
      <>
        <Header />
        <Page>
          <ReadingColumn wide>
            <Title />
            {query.isError ? (
              <LoadError
                className="mt-6"
                title="Não conseguimos carregar o questionário"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            ) : (
              <div role="status" aria-label="Carregando" className="mt-6 flex flex-col gap-4">
                <Skeleton className="h-[96px] w-full" />
                <Skeleton className="h-[360px] w-full" />
                <Skeleton className="h-[220px] w-full" />
                <Skeleton className="h-[220px] w-full" />
                <Skeleton className="h-[120px] w-full" />
              </div>
            )}
          </ReadingColumn>
        </Page>
      </>
    )
  }

  // Outra versão das perguntas da clínica, outro formulário: o que foi carregado é a base da edição.
  return (
    <QuestionnaireForm
      key={query.data.clinicQuestionVersionId ?? 'none'}
      questionnaire={query.data}
      editable={isDesk}
      savedAt={savedAt}
      onPublished={(at) => setSavedAt(at)}
      onReload={() => void query.refetch()}
    />
  )
}

function Header({ aside, actions }: { aside?: ReactNode; actions?: ReactNode }) {
  return (
    <PageHeader
      LinkComponent={Link}
      crumbs={[
        { label: 'Operação' },
        { label: 'Anamneses', href: routes.anamneses },
        { label: 'Questionário' },
      ]}
      aside={aside}
      actions={actions}
    />
  )
}

function Title({ questionnaire }: { questionnaire?: Questionnaire }) {
  return (
    <PageTitle
      title="Questionário da anamnese"
      subtitle={
        questionnaire
          ? [
              `${questionCountLine(questionnaire.questionCount)} em 4 blocos`,
              `padrão da Westhetics versão ${questionnaire.questionnaireVersionNumber ?? '—'}`,
              `${questionnaire.clinicQuestionCount} ${questionnaire.clinicQuestionCount === 1 ? 'pergunta' : 'perguntas'} da clínica`,
              'a paciente responde no celular, em cerca de 4 minutos.',
            ].join(' · ')
          : 'O que a paciente responde no celular antes do atendimento.'
      }
    />
  )
}

function QuestionnaireForm({
  questionnaire,
  editable,
  savedAt,
  onPublished,
  onReload,
}: {
  questionnaire: Questionnaire
  /** Abaixo de 900px a tela é só leitura: a edição continua no computador (P-ANA-65). */
  editable: boolean
  savedAt: Date | null
  onPublished: (at: Date) => void
  onReload: () => void
}) {
  const publish = usePublishClinicQuestions()
  const health = questionnaire.sections.find((section) => section.key === 'yourHealth')
  const defaultHealth = health?.questions.filter((question) => question.source !== 'clinic') ?? []
  const loaded = health?.questions.filter((question) => question.source === 'clinic') ?? []

  const [questions, setQuestions] = useState<QuestionDraft[]>(() => loaded.map(fromQuestion))
  const [expanded, setExpanded] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  function change(next: QuestionDraft[]) {
    setQuestions(next)
    setDirty(true)
    if (publish.isError) publish.reset()
  }

  function add() {
    const question = emptyQuestion('yes_no')
    change([...questions, question])
    setExpanded(question.uid)
  }

  function discard() {
    setQuestions(loaded.map(fromQuestion))
    setDirty(false)
    setSubmitted(false)
    setExpanded(null)
    publish.reset()
  }

  const takenKeys = (uid: string) =>
    new Set(questions.filter((q) => q.uid !== uid && q.key.trim()).map((q) => q.key.trim()))
  const firstInvalid = questions.find(
    (question) =>
      Object.keys(
        validateQuestion(question, {
          isClinic: true,
          previous: [],
          takenKeys: takenKeys(question.uid),
        }),
      ).length > 0,
  )

  function submit() {
    setSubmitted(true)
    if (firstInvalid) {
      setExpanded(firstInvalid.uid)
      return
    }
    publish.mutate(toClinicQuestionsInput(questions, questionnaire.clinicQuestionVersionId), {
      onSuccess: (version) => {
        toast('Perguntas publicadas — valem para as próximas anamneses')
        onPublished(version.publishedAt ? new Date(version.publishedAt) : new Date())
        setDirty(false)
        setSubmitted(false)
        setExpanded(null)
      },
      onError: (error) => {
        // O 422 aponta a pergunta pelo índice: ela abre com a mensagem embaixo do campo.
        const index = Object.keys(fieldErrors(error))
          .map((path) => /^questions\.(\d+)/.exec(path)?.[1])
          .find(Boolean)
        if (index !== undefined) setExpanded(questions[Number(index)]?.uid ?? null)
      },
    })
  }

  const conflict = publish.error && isApiError(publish.error) && publish.error.code === 'conflict'
  const serverFields = publish.isError && !conflict ? fieldErrors(publish.error) : {}
  const hasFieldError = Object.keys(serverFields).length > 0
  const sections = questionnaire.sections
  // A posição segue a numeração do padrão: as da clínica vêm depois (RN-ANA-09).
  const clinicAsQuestions: Question[] = questions.map((draft, index) =>
    draftAsQuestion(draft, defaultHealth.length + index + 1, 'clinic'),
  )

  return (
    <>
      {/* A ficha em branco é ação geral (T35): fica no cabeçalho, não no rodapé do formulário. */}
      <Header actions={<PrintBlankFormButton />} />
      <Page>
        <ReadingColumn wide>
          <Title questionnaire={questionnaire} />

          <div className="mt-6 flex flex-col gap-4">
            {editable ? null : (
              <AttentionBlock variant="lilac" title="A edição das perguntas continua no computador">
                Aqui o questionário aparece como a paciente o verá. Para acrescentar, reordenar ou
                remover as perguntas da clínica, abra esta tela numa janela com mais de 900px.
              </AttentionBlock>
            )}

            {conflict ? (
              <ActionError
                title="As perguntas foram alteradas em outra edição"
                description={`${errorMessage(publish.error)} O que você alterou continua na tela até recarregar.`}
                actions={
                  <Button variant="outline" size="sm" onClick={onReload}>
                    Recarregar as perguntas
                  </Button>
                }
              />
            ) : publish.isError && !hasFieldError ? (
              <ActionError
                title="As perguntas não foram publicadas"
                description={`${errorMessage(publish.error)} Nada mudou para as pacientes; o que você alterou continua na tela.`}
                onDismiss={() => publish.reset()}
              />
            ) : null}

            {sections.map((section, index) =>
              section.key === 'yourHealth' ? (
                <SectionReadBlock
                  key={section.key}
                  section={{ ...section, questions: defaultHealth }}
                  index={index}
                >
                  <section
                    aria-labelledby="clinic-questions-title"
                    className="border-hairline mt-5 border-t pt-4"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h3 id="clinic-questions-title" className="eyebrow">
                        Perguntas da clínica · {questions.length} de {CLINIC_LIMIT}
                      </h3>
                      {editable && dirty ? (
                        <Button variant="text" size="sm" onClick={discard}>
                          descartar alterações
                        </Button>
                      ) : null}
                    </div>
                    <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
                      Entram depois das perguntas do padrão. Publicar vale para as próximas
                      anamneses enviadas; as já enviadas, inclusive as não preenchidas, não mudam.
                    </p>

                    {questions.length === 0 ? (
                      <EmptyState
                        className="mt-3"
                        title="Nenhuma pergunta da clínica ainda"
                        description="O padrão já cobre saúde, expectativa e autoimagem. Acrescente só o que a sua clínica precisa saber antes do atendimento."
                        action={
                          editable ? <Button onClick={add}>+ Adicionar pergunta</Button> : undefined
                        }
                      />
                    ) : (
                      <div role="list" aria-label="Perguntas da clínica" className="mt-3">
                        {questions.map((draft, index) => {
                          const question = clinicAsQuestions[index]!
                          if (editable && expanded === draft.uid) {
                            return (
                              <div key={draft.uid} role="listitem" className="py-3">
                                <QuestionForm
                                  question={draft}
                                  mode="clinic"
                                  previous={[]}
                                  takenKeys={takenKeys(draft.uid)}
                                  serverErrors={serverErrorsAt(serverFields, `questions.${index}.`)}
                                  showAllErrors={submitted}
                                  onChange={(next) =>
                                    change(questions.map((q) => (q.uid === next.uid ? next : q)))
                                  }
                                  onClose={() => setExpanded(null)}
                                  onRemove={() => {
                                    change(questions.filter((q) => q.uid !== draft.uid))
                                    setExpanded(null)
                                  }}
                                />
                              </div>
                            )
                          }
                          return (
                            <QuestionRow
                              key={draft.uid}
                              question={question}
                              siblings={clinicAsQuestions}
                              className="px-0"
                              actions={
                                editable ? (
                                  <>
                                    <Button
                                      variant="text"
                                      size="sm"
                                      aria-label={`Editar "${question.label || 'pergunta sem texto'}"`}
                                      onClick={() => setExpanded(draft.uid)}
                                    >
                                      editar
                                    </Button>
                                    <ReorderControls
                                      index={index}
                                      count={questions.length}
                                      itemLabel={question.label || 'pergunta sem texto'}
                                      onMove={(from, to) => change(moveItem(questions, from, to))}
                                    />
                                    <Button
                                      variant="text"
                                      size="sm"
                                      aria-label={`Remover "${question.label || 'pergunta sem texto'}"`}
                                      onClick={() =>
                                        change(questions.filter((q) => q.uid !== draft.uid))
                                      }
                                    >
                                      remover
                                    </Button>
                                  </>
                                ) : undefined
                              }
                            />
                          )
                        })}
                      </div>
                    )}

                    {editable && questions.length > 0 ? (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={questions.length >= CLINIC_LIMIT}
                          onClick={add}
                        >
                          {questions.length >= CLINIC_LIMIT
                            ? `Limite de ${CLINIC_LIMIT} perguntas`
                            : '+ Adicionar pergunta'}
                        </Button>
                      </div>
                    ) : null}
                  </section>
                </SectionReadBlock>
              ) : (
                <SectionReadBlock key={section.key} section={section} index={index} />
              ),
            )}

            <ConfirmationReadBlock
              declarationText={questionnaire.declaration.text}
              index={sections.length}
            />

            {editable ? (
              <p className="font-body text-label text-right text-[12px] leading-[1.6] font-light">
                {dirty && questions.length === 0 && loaded.length > 0
                  ? 'Publicar com a lista vazia desliga as perguntas da clínica nas próximas anamneses. '
                  : ''}
                Cada publicação é uma versão nova das perguntas da clínica. O que já foi enviado não
                muda.
              </p>
            ) : null}
            {editable ? (
              <FormFooter
                status={
                  <SavedIndicator savedAt={savedAt} dirty={dirty} saving={publish.isPending} />
                }
                primary={
                  <Button
                    disabled={!dirty}
                    loading={publish.isPending}
                    loadingLabel="Publicando as perguntas…"
                    onClick={submit}
                  >
                    {dirty ? 'Publicar perguntas' : 'Nenhuma alteração para publicar'}
                  </Button>
                }
              />
            ) : null}
          </div>
        </ReadingColumn>
      </Page>
    </>
  )
}
